/* Copyright (c) 2024 Xperi. All Rights Reserved. */
import { ContentScreenDetails } from 'client-code/actions/dist/models/content-screen-details';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { ActionsServiceRepositoryImpl } from '../impls/ActionsServiceRepositoryImpl';
import { ActionsServiceRepositoryImplV2 } from '../impls/ActionsServiceRepositoryImplV2';
import { IActionsService } from '../interfaces/IActionsService';
import { IActionsServiceRepository } from '../interfaces/IActionsServiceRepository';
import { ActionsServiceV1Contract, ActionsServiceV2Contract } from '../servicecontracts';
import {
    TActionList,
    TActionsContentParams,
    TActionsContentScreenContentIdParams,
    TActionsContentScreenDetailsContentIdParamsV2,
    TActionsContentScreenDetailsVodOfferParamsV2,
    TActionsContentScreenOfferParams,
    TActionsContentScreenRecordingParams,
    TActionsParams,
    TActionsParamsUCScreenDetails,
    TActionsRecordingParams,
    TActionsUnifiedCollectionParams,
    TActionsVodOfferParams,
    TPlayNextContentActionsParams,
    TPlayNextRecordingActionsParams,
    TServicesParams,
    TUnifiedCollectionScreenDetails,
} from '../types';
import { CcbSwitch } from '../utils/ccb';
import { CcbSwitchName } from '../utils/ccb/enums';

export class ActionsService implements IActionsService {
    private static cache = new Map<string, any>();

    // eslint-disable-next-line max-len
    private oActionsServiceRepository: IActionsServiceRepository<ActionsServiceV1Contract>;

    // eslint-disable-next-line max-len
    private oActionsServiceRepositoryV2: IActionsServiceRepository<ActionsServiceV2Contract>;

    public constructor(params: TServicesParams) {
        this.oActionsServiceRepository = new ActionsServiceRepositoryImpl(params);
        this.oActionsServiceRepositoryV2 = new ActionsServiceRepositoryImplV2(params);
    }

    private static getCacheKey(methodName: string, params: Record<string, any>): string {
        const { actionsNetworkCallId, ...rest } = params;
        const sortedKeys = ['methodName', ...Object.keys(rest).sort()];
        return JSON.stringify({ methodName, ...rest }, sortedKeys);
    }

    // eslint-disable-next-line class-methods-use-this
    private async resolveCached<T>(
        methodName: string,
        params: Record<string, any>,
        fetcher: () => Promise<T>,
    ): Promise<T> {
        if (params.screenType !== APPLICATION_FEATURE_AREA.PLAYER) {
            const result = await fetcher();
            return result;
        }

        const cacheKey = ActionsService.getCacheKey(methodName, params);
        if (ActionsService.cache.has(cacheKey)) {
            return ActionsService.cache.get(cacheKey) as T;
        }
        const result = await fetcher();
        ActionsService.cache.set(cacheKey, result);
        return result;
    }

    // eslint-disable-next-line class-methods-use-this
    private removeCastAndMayAlsoLike(contentScreenDetailsOfferResponse: any) {
        const responseData = contentScreenDetailsOfferResponse;
        const isCastEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_CAST_SLT_REMOVE_IN_KST_12418,
        );

        if (isCastEnabled) {
            responseData.detailList.details = responseData.detailList.details.filter(
                ({ presentation }: { presentation: any }) =>
                    presentation.presentationId !== 'MAY_ALSO_LIKE_DETAIL',
            );
        } else {
            responseData.detailList.details = responseData.detailList.details.filter(
                ({ presentation }: { presentation: any }) =>
                    presentation.presentationId !== 'MAY_ALSO_LIKE_DETAIL' &&
                    presentation.presentationId !== 'CAST_DETAIL',
            );
        }
        return responseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsContentActions(
        actionParams: TActionsContentScreenContentIdParams,
    ) {
        const contentScreenDetailsContentIdResponse = await this.resolveCached(
            'fetchContentScreenDetailsContentActions',
            actionParams,
            () =>
                this.oActionsServiceRepository.fetchContentScreenDetailsContentActions(
                    actionParams,
                ),
        );
        const filteredResponseData = this.removeCastAndMayAlsoLike(
            contentScreenDetailsContentIdResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsVodOfferActionsV2(
        actionParams: TActionsContentScreenDetailsVodOfferParamsV2,
    ) {
        const contentScreenDetailsVodOfferResponse = await this.resolveCached(
            'fetchContentScreenDetailsVodOfferActionsV2',
            actionParams,
            () =>
                this.oActionsServiceRepositoryV2.fetchContentScreenDetailsVodOfferActions(
                    actionParams,
                ),
        );
        const filteredResponseData = this.removeCastAndMayAlsoLike(
            contentScreenDetailsVodOfferResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsOfferActions(
        actionsParams: TActionsContentScreenOfferParams,
    ) {
        const contentScreenDetailsContentIdResponse = await this.resolveCached(
            'fetchContentScreenDetailsOfferActions',
            actionsParams,
            () =>
                this.oActionsServiceRepository.fetchContentScreenDetailsOfferActions(actionsParams),
        );
        const filteredResponseData = this.removeCastAndMayAlsoLike(
            contentScreenDetailsContentIdResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsRecordingActions(
        actionsParams: TActionsContentScreenRecordingParams,
    ) {
        const contentScreenDetailsContentIdResponse = await this.resolveCached(
            'fetchContentScreenDetailsRecordingActions',
            actionsParams,
            () =>
                this.oActionsServiceRepository.fetchContentScreenDetailsRecordingActions(
                    actionsParams,
                ),
        );
        const filteredResponseData = this.removeCastAndMayAlsoLike(
            contentScreenDetailsContentIdResponse,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getOfferActions(actionsParams: TActionsParams): Promise<TActionList> {
        return this.resolveCached(
            'fetchOfferActions',
            actionsParams,
            () => this.oActionsServiceRepository.fetchOfferActions(actionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public async getOfferActionsV2(actionsParams: TActionsParams): Promise<TActionList> {
        return this.resolveCached(
            'fetchOfferActionsV2',
            actionsParams,
            () => this.oActionsServiceRepositoryV2.fetchOfferActions(actionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public async getRecordingActionsV2(
        actionsParams: TActionsRecordingParams,
    ): Promise<TActionList> {
        return this.resolveCached(
            'fetchRecordingActionsV2',
            actionsParams,
            () => this.oActionsServiceRepositoryV2.fetchRecordingActions(actionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public async getContentScreenDetailsContentActionsV2(
        actionsParams: TActionsContentScreenDetailsContentIdParamsV2,
    ): Promise<ContentScreenDetails> {
        const contentScreenDetailsContentIdResponseV2 = await this.resolveCached(
            'fetchContentScreenDetailsContentActionsV2',
            actionsParams,
            () =>
                this.oActionsServiceRepositoryV2.fetchContentScreenDetailsContentActions(
                    actionsParams,
                ),
        );
        const filteredResponseData = this.removeCastAndMayAlsoLike(
            contentScreenDetailsContentIdResponseV2,
        );
        return filteredResponseData;
    }

    /**
     * @inheritdoc
     */
    public async getUnifiedCollectionActions(
        actionsParams: TActionsUnifiedCollectionParams,
    ): Promise<TActionList> {
        return this.resolveCached(
            'fetchUnifiedCollectionActions',
            actionsParams,
            () => this.oActionsServiceRepository.fetchUnifiedCollectionActions(actionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public getUCScreenDetailsActions(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        return this.resolveCached(
            'fetchUnifiedCollectionScreenDetailsActions',
            actionsParams,
            () =>
                this.oActionsServiceRepository.fetchUnifiedCollectionScreenDetailsActions(
                    actionsParams,
                ),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public getUCScreenDetailsActionsV2(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        return this.resolveCached(
            'fetchUnifiedCollectionScreenDetailsActionsV2',
            actionsParams,
            () =>
                this.oActionsServiceRepositoryV2.fetchUnifiedCollectionScreenDetailsActions(
                    actionsParams,
                ),
        );
    }

    /**
     * @inheritdoc
     */
    public async getVodOfferActionsV2(actionsParams: TActionsVodOfferParams): Promise<TActionList> {
        return this.resolveCached(
            'fetchVodOfferActionsV2',
            actionsParams,
            () => this.oActionsServiceRepositoryV2.fetchVodOfferActions(actionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public async getContentActions(actionsParams: TActionsContentParams): Promise<TActionList> {
        return this.resolveCached(
            'fetchContentActions',
            actionsParams,
            () => this.oActionsServiceRepository.fetchContentActions(actionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public async getPlayNextContentActions(
        playNextActionsParams: TPlayNextContentActionsParams,
    ): Promise<TActionList> {
        return this.resolveCached(
            'fetchPlayNextContentActions',
            playNextActionsParams,
            () => this.oActionsServiceRepository.fetchPlayNextContentActions(playNextActionsParams),
            // eslint-disable-next-line function-paren-newline
        );
    }

    /**
     * @inheritdoc
     */
    public async getPlayNextRecordingActions(
        playNextRecordingActionsParams: TPlayNextRecordingActionsParams,
    ): Promise<TActionList> {
        return this.resolveCached(
            'fetchPlayNextRecordingActions',
            playNextRecordingActionsParams,
            () =>
                this.oActionsServiceRepository.fetchPlayNextRecordingActions(
                    playNextRecordingActionsParams,
                ),
        );
    }

    public clearCache(): void {
        ActionsService.cache.clear();
    }
}
